import { CameraIcon } from "@heroicons/react/24/outline";
import { useMemo, useState, type MouseEvent } from "react";

import usePartDetails from "../../data/usePartDetails";
import { IPart, IPartImage } from "../../models/Part";
import classNames from "../../utils/classNames";
import LoadingImage from "../shared/LoadingImage";
import StockImageModal, {
  type StockLightboxImage,
} from "./viewStock/StockImageModal";

type ResolvedPartImage = { thumbnail: string; full: string };

function buildLightboxFromPartImages(images: IPartImage[]): StockLightboxImage[] {
  return images
    .filter((i) => (i.url || i.thumbnailUrl) && !i.deleted)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((img, i) => ({
      id: img.id,
      url: img.url || img.thumbnailUrl || "",
      alt: `Part image ${i + 1}`,
    }));
}

export default function PartImage({
  part,
  width = "w-48",
  /** Fill the table cell width; image uses 100% of the cell (minus padding). */
  fillCell = false,
}: {
  part: IPart | null | undefined;
  width?: string;
  fillCell?: boolean;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [fullImageLoaded, setFullImageLoaded] = useState(false);

  /** List rows often only include a preview image; load full set when the lightbox opens. */
  const { partDetails } = usePartDetails(part?.id, lightboxOpen && !!part?.id);

  const { displayImage, lightboxFromRow, initialRowIndex } = useMemo(() => {
    if (!part) {
      return {
        displayImage: null as ResolvedPartImage | null,
        lightboxFromRow: [] as StockLightboxImage[],
        initialRowIndex: 0,
      };
    }
    const list = Array.isArray(part.images) ? part.images : [];
    const sorted = list
      .filter((i) => (i.url || i.thumbnailUrl) && !i.deleted)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (sorted.length > 0) {
      const fromGallery =
        list.find((i) => i.isThumbnail === true) ?? sorted[0];
      const initialRowIndex = Math.max(
        0,
        sorted.findIndex((s) => s.id === fromGallery.id)
      );
      const thumb = fromGallery.thumbnailUrl || fromGallery.url;
      const full = fromGallery.url || fromGallery.thumbnailUrl;
      const lightboxFromRow: StockLightboxImage[] = sorted.map((img, i) => ({
        id: img.id,
        url: img.url || img.thumbnailUrl || "",
        alt: `Part image ${i + 1}`,
      }));
      return {
        displayImage: { thumbnail: thumb, full: full! } as ResolvedPartImage,
        lightboxFromRow,
        initialRowIndex,
      };
    }

    if (part.cdnThumbnail) {
      const u = part.cdnThumbnail;
      return {
        displayImage: { thumbnail: u, full: u },
        lightboxFromRow: [{ url: u, alt: "Part image" }],
        initialRowIndex: 0,
      };
    }
    if (part.ebayThumbnail) {
      const u = part.ebayThumbnail;
      return {
        displayImage: { thumbnail: u, full: u },
        lightboxFromRow: [{ url: u, alt: "Part image" }],
        initialRowIndex: 0,
      };
    }
    return {
      displayImage: null,
      lightboxFromRow: [],
      initialRowIndex: 0,
    };
  }, [part]);

  const lightboxImages = useMemo(() => {
    const fromDetails = partDetails?.images;
    if (Array.isArray(fromDetails) && fromDetails.length > 0) {
      const built = buildLightboxFromPartImages(fromDetails);
      if (built.length > 0) return built;
    }
    return lightboxFromRow;
  }, [partDetails, lightboxFromRow]);

  const initialCarouselIndex = useMemo(() => {
    if (lightboxImages.length === 0) return 0;
    const fromDetails = partDetails?.images;
    if (Array.isArray(fromDetails) && fromDetails.length > 0) {
      const sorted = fromDetails
        .filter((i) => (i.url || i.thumbnailUrl) && !i.deleted)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const thumb = sorted.find((i) => i.isThumbnail) ?? sorted[0];
      if (thumb?.id) {
        const idx = lightboxImages.findIndex((m) => m.id === thumb.id);
        if (idx >= 0) return idx;
      }
    }
    return initialRowIndex;
  }, [partDetails, lightboxImages, initialRowIndex]);

  const lightboxTitle = part?.tagNumber
    ? `Part ${part.tagNumber}`
    : "Part images";

  const handleMouseEnter = () => {
    setHovered(true);
    setFullImageLoaded(false);
  };
  const handleMouseLeave = () => {
    setHovered(false);
  };
  const handleMouseMove = (e: MouseEvent) => {
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleOpenLightbox = (e: MouseEvent) => {
    e.stopPropagation();
    if (!part?.id) return;
    setHovered(false);
    setLightboxOpen(true);
  };

  return (
    <>
      <span
        className={classNames(
          "relative flex",
          fillCell ? "w-full" : "justify-center"
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        {displayImage ? (
          <button
            type="button"
            title={
              part?.id
                ? lightboxFromRow.length > 1
                  ? "View all images"
                  : "View images"
                : undefined
            }
            aria-label={part?.id ? "Open image viewer" : undefined}
            className={classNames(
              "m-0 border-0 bg-transparent p-0",
              fillCell && "w-full",
              part?.id &&
                "cursor-pointer rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            )}
            onClick={handleOpenLightbox}
            disabled={!part?.id}
          >
            <img
              className={classNames(width, "h-auto object-contain", fillCell && "max-w-none")}
              src={displayImage.thumbnail}
              alt=""
            />
          </button>
        ) : (
          <span
            className={classNames(
              fillCell ? "flex w-full justify-center py-2" : "inline-flex"
            )}
          >
            <CameraIcon
              className={classNames(
                "text-gray-600",
                fillCell ? "h-16 w-16" : "h-14 w-14"
              )}
              aria-hidden
            />
          </span>
        )}
      </span>

      {part?.id && (
        <StockImageModal
          open={lightboxOpen}
          setOpen={setLightboxOpen}
          images={lightboxImages}
          initialIndex={initialCarouselIndex}
          title={lightboxTitle}
          part={partDetails ?? part}
        />
      )}

      {/* Full-size hover image */}
      {hovered && displayImage && (
        <div
          className="fixed pointer-events-none z-50"
          style={{
            top: mousePos.y + 10,
            left: mousePos.x + 10,
          }}
        >
          {!fullImageLoaded && (
            <div className="w-16 h-16">
              <LoadingImage />
            </div>
          )}
          <img
            src={displayImage.full}
            alt=""
            className={`max-w-2xl max-h-xl border border-gray-300 shadow-lg rounded-md transition-opacity duration-300 ${
              fullImageLoaded ? "opacity-100" : "opacity-0"
            }`}
            onLoad={() => setFullImageLoaded(true)}
          />
        </div>
      )}
    </>
  );
}
